export type AgentHubJobState =
  | "queued"
  | "working"
  | "needs_input"
  | "awaiting_approval"
  | "completed"
  | "failed";

export type AgentHubResultArtifact = {
  id: string;
  filename: string;
  content_type?: string;
  byte_size?: number;
};

export type AgentHubJob = {
  jobId: string;
  state: AgentHubJobState;
  title?: string;
  result?: string;
  error?: string;
  progress?: string;
  resultArtifacts: AgentHubResultArtifact[];
  pollError?: string;
};

const JOB_STATES = new Set<AgentHubJobState>([
  "queued",
  "working",
  "needs_input",
  "awaiting_approval",
  "completed",
  "failed",
]);

export function submittedAgentHubJobResult(
  toolName: string | undefined,
  value: unknown
): unknown {
  if (toolName === "submit_codex_job") return value;
  return null;
}

function optionalText(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

export function normalizeAgentHubJob(value: unknown): AgentHubJob | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  const jobId = optionalText(raw.job_id) || optionalText(raw.id);
  if (!jobId) return null;

  const rawState = optionalText(raw.state) || optionalText(raw.status) || "queued";
  const state = JOB_STATES.has(rawState as AgentHubJobState)
    ? (rawState as AgentHubJobState)
    : "queued";
  const messages = Array.isArray(raw.messages) ? raw.messages : [];
  const latestMessage = [...messages].reverse().find((message) => {
    return Boolean(
      message &&
        typeof message === "object" &&
        optionalText((message as Record<string, unknown>).text)
    );
  }) as Record<string, unknown> | undefined;
  const artifacts = Array.isArray(raw.result_artifacts) ? raw.result_artifacts : [];

  return {
    jobId,
    state,
    title: optionalText(raw.title),
    result: optionalText(raw.result),
    error: optionalText(raw.error),
    progress: latestMessage ? optionalText(latestMessage.text) : undefined,
    resultArtifacts: artifacts.flatMap((artifact) => {
      if (!artifact || typeof artifact !== "object") return [];
      const item = artifact as Record<string, unknown>;
      const id = optionalText(item.id);
      const filename = optionalText(item.filename);
      if (!id || !filename) return [];
      return [
        {
          id,
          filename,
          content_type: optionalText(item.content_type),
          byte_size: typeof item.byte_size === "number" ? item.byte_size : undefined,
        },
      ];
    }),
  };
}

export function mergeAgentHubJob(
  current: AgentHubJob | undefined,
  incoming: AgentHubJob
): AgentHubJob {
  if (!current) return incoming;
  return {
    ...current,
    ...incoming,
    title: incoming.title || current.title,
    result: incoming.result || current.result,
    error: incoming.error || current.error,
    progress: incoming.progress || current.progress,
    resultArtifacts:
      incoming.resultArtifacts.length > 0
        ? incoming.resultArtifacts
        : current.resultArtifacts,
    pollError: undefined,
  };
}

export function isTerminalAgentHubState(state: AgentHubJobState) {
  return state === "completed" || state === "failed";
}

export function agentHubStateLabel(state: AgentHubJobState) {
  switch (state) {
    case "queued":
      return "Queued";
    case "working":
      return "Codex is working";
    case "needs_input":
      return "Needs your input";
    case "awaiting_approval":
      return "Awaiting approval";
    case "completed":
      return "Completed";
    case "failed":
      return "Failed";
  }
}
